
import { Box, Button, Card, CardActions, CardContent, CardHeader, Collapse, Stack, Typography, useTheme } from "@mui/material";
import { ReactNode, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { ReportDeliveryType, ReportTypeEnum } from "../../services/reporting-data-service/ReportScheduleModels";
import { ScheduleSummaryCard } from "./ScheduleSummaryCard";
import { ScheduleNotificationsCard } from "./ScheduleNotificationsCard";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { ScheduledReportsActionEnum, ScheduledReportsItem } from "./ScheduledReportsListUtils";
import ActionMenu from "../action-menu/ActionMenu";
import { faCog  } from "@elynx/free-solid-svg-icons";
import { faFile} from "@elynx/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileSpreadsheet } from "@elynx/pro-regular-svg-icons";
import { ActionMenuItem, BaseActionEnum, BaseActions } from "../action-menu/ActionMenuItem";

export type ScheduledReportsListItemProps = {
    item: ScheduledReportsItem,
    onAction: (action: BaseActions | ScheduledReportsActionEnum, payload: ScheduledReportsItem) => void,
};

export function ScheduledReportsListItem({
    item,
    onAction,
}: ScheduledReportsListItemProps) {

    const [expanded, setExpanded] = useState(false);
    const [notificationsExpanded, setNotificationsExpanded] = useState(false);
    const theme = useTheme();
    const { t } = useTranslation();

    const actionItems: ActionMenuItem[] = useMemo(() => {

        let actions = [{
            label: t("scheduledReports.edit"),
            action: BaseActionEnum.Edit,
            onClick: () => {
                onAction(BaseActionEnum.Edit, item);
            },
        },{
            label: t("scheduledReports.copy"),
            action: BaseActionEnum.Copy,
            onClick: () => {
                onAction(BaseActionEnum.Copy, item);
            },
        },{
            label: t("scheduledReports.delete"),
            action: BaseActionEnum.Delete,
            onClick: () => {
                onAction(BaseActionEnum.Delete, item);
            },
        },{
            label: t("actions.run"),
            action: BaseActionEnum.Run,
            onClick: () => {
                onAction(BaseActionEnum.Run, item);
            },
        }];

        return actions;
    }, [
        t,
        onAction,
        item
    ]);

    const recipients = item.notifications.filter(o => o.deliveryType === ReportDeliveryType.Email);
    const notificationLists = item.notifications.filter(o => o.deliveryType === ReportDeliveryType.NotificationList);

    function getTitle(): ReactNode {
        return (
            <Box>
                {item.name}
            </Box>
        );
    }

    function getSubheader(): ReactNode {
        return (
            <Box>
                {item.reportName}
            </Box>
        );
    }

    function getAvatar() {
        return (
            <Box sx={{
                position: "relative",
                width: (theme) => theme.spacing(4),
                display: 'flex'
            }}
            >
                {item.type === ReportTypeEnum.GroupMarketing ?
                <i className='sw sw-money-graph fa-lg half-right' style={{ fontSize: '2rem', marginLeft: "2px" }} /> : 
                item.type === ReportTypeEnum.Custom ?
                <Box
                    className="fa-layers fa-fw"
                    sx={{
                        position: 'relative',
                        marginRight: theme.spacing(2)
                    }}>
                    <FontAwesomeIcon icon={faFile} size="2x" color={theme.palette.grey[800]} />
                        <FontAwesomeIcon icon={faCog} 
                            style={{
                                top: '1px',
                                left: '3px'
                            }}
                            color={theme.palette.grey[800]}
                            //color={'green'}
                            transform="shrink-1" 
                        />
                    </Box>
                 :

                <FontAwesomeIcon icon={faFileSpreadsheet} fixedWidth={true} size="2x" />
                }
            </Box>
        );
    }

    return (
        <Card variant="outlined" sx={{minHeight: '4rem'}}>
            <CardHeader
                sx={{
                    padding: 1,
                    paddingBottom: 0
                }}
                title={getTitle()}
                avatar={getAvatar()}
                subheader={getSubheader()}
                action={<ActionMenu actionItems={actionItems} />}
            />
            
            <CardContent sx={{marginLeft: theme.spacing(5)}}>
                <Stack direction='column'>
                    <Typography variant='body2'>
                        {t('scheduledReports.reportCreatedBy', {name: item.configCreatedBy?.objectName})}
                    </Typography>
                    <Typography variant='body2'>
                        {t('scheduledReports.taskCreatedBy', {name: item.taskCreatedBy?.objectName})}
                    </Typography>
                    <Typography variant='body2'>
                        {t('scheduledReports.group', {group: item.groupPath})}
                    </Typography>
                    
                </Stack>
            </CardContent>

            {item.schedules.length > 0 && 
            <CardActions sx={{ paddingTop: 0, paddingBottom: 0 }}>

                <Button
                    sx={{
                        color: "text.primary"
                    }}
                    variant="text"
                    onClick={() => { setExpanded(!expanded)}}
                    style={{ marginLeft: "auto" }}
                    endIcon={<ExpandMoreIcon sx={{
                        transform: !expanded ? "rotate(0deg)" : "rotate(180deg)",
                        transition: theme.transitions.create('transform', {
                            duration: theme.transitions.duration.shortest,
                        })
                    }} />}
                >
                    {t('scheduledReports.schedules', {count: item.schedules.length})}
                </Button>

            </CardActions>
            }


            <Collapse in={expanded} timeout="auto">
                <CardContent sx={{
                    padding: (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important`,
                    minHeight: theme.spacing(4)
                }}>

                    {item.schedules.map((o, ndx) => 
                        <ScheduleSummaryCard key={ndx} item={o} />
                    )}
                </CardContent>
            </Collapse>


            {item.notifications.length > 0 && 
            <CardActions sx={{ paddingTop: 0, paddingBottom: 0 }}>

                <Button
                    sx={{
                        color: "text.primary"
                    }}
                    variant="text"
                    onClick={() => { setNotificationsExpanded(!notificationsExpanded)}
                    }
                    style={{ marginLeft: "auto" }}
                    endIcon={<ExpandMoreIcon sx={{
                        transform: !notificationsExpanded ? "rotate(0deg)" : "rotate(180deg)",
                        transition: theme.transitions.create('transform', {
                            duration: theme.transitions.duration.shortest,
                        })
                    }} />}
                >
                    {t('scheduledReports.notifications', {count: item.notifications.length})}
                </Button>
            </CardActions>
            }



            <Collapse in={notificationsExpanded} timeout="auto">
                <CardContent sx={{
                    padding: '0!important',
                    margin: (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important`,
                    minHeight: theme.spacing(4),
                    maxHeight: 300,
                    overflow: 'hidden',
                    overflowY: 'auto'
                }}>

                    {notificationLists.length > 0 &&
                        <ScheduleNotificationsCard 
                            title={getObjectTypeName(eLynxObjectTypeEnum.NotificationList, true)} 
                            items={notificationLists}
                        />
                    }

                    {recipients.length > 0 &&
                        <ScheduleNotificationsCard 
                            title={getObjectTypeName(eLynxObjectTypeEnum.Recipient, true)} 
                            items={recipients}
                        />
                    }

                </CardContent>
            </Collapse>


        </Card>
    );
}
